import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { adminApi, publicApi } from "../../lib/api";

const isThisMonth = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

export default function DashboardSummary() {
  const [stats, setStats] = useState({ quotesMonth: 0, quotesTotal: 0, subscribers: 0, enquiriesMonth: 0, guidesMonth: 0, guidesTotal: 0, leadsMonth: 0, rating: null, reviews: null });
  const [enquiries, setEnquiries] = useState([]);
  const [range, setRange] = useState("all"); // "all" | "month"

  useEffect(() => {
    Promise.all([
      adminApi.quotes.list().catch(() => []),
      adminApi.subscribers.list().catch(() => []),
      adminApi.enquiries.list().catch(() => []),
      adminApi.guideLeads.list().catch(() => []),
      publicApi.googleRating().catch(() => ({})),
    ]).then(([quotes, subs, enquiriesList, guideLeads, google]) => {
      const quotesMonth = quotes.filter((q) => isThisMonth(q.created_at)).length;
      const enquiriesMonth = enquiriesList.filter((e) => isThisMonth(e.created_at)).length;
      const guidesMonth = guideLeads.filter((g) => isThisMonth(g.created_at)).length;
      setStats({
        quotesMonth,
        quotesTotal: quotes.length,
        subscribers: subs.length,
        enquiriesMonth,
        guidesMonth,
        guidesTotal: guideLeads.length,
        leadsMonth: quotesMonth + enquiriesMonth + guidesMonth,
        rating: google?.rating ?? null,
        reviews: google?.total ?? null,
      });
      setEnquiries(enquiriesList);
    });
  }, []);

  const scoped = range === "month" ? enquiries.filter((e) => isThisMonth(e.created_at)) : enquiries;
  const tally = {};
  scoped.forEach((e) => {
    const list = (e.services && e.services.length ? e.services : (e.service ? [e.service] : []));
    list.forEach((s) => { const k = (s || "").trim(); if (k) tally[k] = (tally[k] || 0) + 1; });
  });
  const sources = Object.entries(tally).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  const enquiryTotal = scoped.length;

  return (
    <div data-testid="admin-dashboard" className="mt-6">
      <div data-testid="top-enquiry-sources" className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange/10 text-orange flex items-center justify-center"><TrendingUp size={20} /></div>
            <div>
              <h2 className="font-display font-bold text-navy">Most requested services</h2>
              <p className="text-xs text-slate-400">Which services enquirers ask about most ({range === "month" ? "this month" : "all time"} — {enquiryTotal} enquir{enquiryTotal === 1 ? "y" : "ies"}).</p>
            </div>
          </div>
          <div className="flex bg-slate-100 rounded-full p-1 text-xs font-semibold shrink-0">
            <button data-testid="sources-range-month" onClick={() => setRange("month")} className={`px-3.5 py-1.5 rounded-full transition-colors ${range === "month" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>This month</button>
            <button data-testid="sources-range-all" onClick={() => setRange("all")} className={`px-3.5 py-1.5 rounded-full transition-colors ${range === "all" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>All time</button>
          </div>
        </div>

        {sources.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">{range === "month" ? "No service enquiries yet this month." : "No service enquiries yet. As people submit the contact form, their selected services will appear here."}</p>
        ) : (
          <div className="space-y-3 mt-5">
            {sources.map((s, i) => {
              const pct = Math.round((s.count / sources[0].count) * 100);
              return (
                <div key={s.name} data-testid={`source-${s.name}`} className="flex items-center gap-4">
                  <span className="w-5 text-xs font-bold text-slate-300 shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-navy truncate">{s.name}</span>
                      <span className="text-xs font-bold text-slate-500 shrink-0 ml-3">{s.count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-orange transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
